import {AdyenPaymentsService} from '../../../../../../services/adyen/payments'
import {AdyenPaymentsDetailsService} from '@adyenPWA/lib/services/payments-details'
import {executeCallbacks} from '@adyenPWA/lib/utils/executeCallbacks'
import {getCurrencyValueForApi} from '@adyenPWA/lib/utils/parsers.mjs'
import {CART_HREF} from '../../../../constants.js'

// Utils
import {getConvertedCurrencyCode, getConvertedPrice} from '../../../../utils/price-utils'

export const baseConfig = ({
    beforeSubmit = [],
    afterSubmit = [],
    beforeAdditionalDetails = [],
    afterAdditionalDetails = [],
    onError = () => {
        props.onNavigate('/checkout/error')
    },
    ...props
}) => {
    return {
        amount: getAmount(props),
        onSubmit: executeCallbacks([...beforeSubmit, onSubmit, ...afterSubmit], props, onError),
        onAdditionalDetails: executeCallbacks(
            [...beforeAdditionalDetails, onAdditionalDetails, ...afterAdditionalDetails],
            props,
            onError
        ),
        setStatusAutomatically: false
    }
}

export const onSubmit = async (state, component, props) => {
    if (!state.isValid) {
        throw new Error('invalid state')
    }
    // If billing address form has errors or terms and conditions is not checked,
    // we do not process the payment but stay on the same page
    if (
        state.termsAndConditionsInvalid ||
        state.billingAddressInvalid ||
        state.insufficientInventory ||
        state.pendingShippingAdjustment
    ) {
        if (state.data.paymentMethod.type === 'paypal') {
            // For PayPal, trigger error status and then ready status immediately if insufficient inventory
            component.setStatus('error')
            if (state.insufficientInventory) {
                component.setStatus('ready')
                props.onNavigate(CART_HREF)
            } else {
                setTimeout(() => {
                    component.setStatus('ready')
                }, 3000)
            }
        } else {
            component.setStatus('ready')
            if (state.insufficientInventory) {
                // Always navigate to cart if there is insufficient inventory
                props.onNavigate(CART_HREF)
            }
        }
        return
    }
    component.setStatus('loading')

    const adyenPaymentService = new AdyenPaymentsService(props?.token, props?.site)
    const paymentsResponse = await adyenPaymentService.submitPayment(
        {
            ...state.data,
            origin: state.data.origin ? state.data.origin : window.location.origin,
            returnUrl: props?.returnUrl || `${window.location.href}/redirect`,
            utmParameters: state.utmParameters,
            endDigits: props?.cardDetails?.endDigits,
            issuerBin: props?.cardDetails?.issuerBin,
            riskifiedBeacon: state.riskifiedBeacon
        },
        props.basket?.basketId,
        props?.customerId
    )
    /*
        When a payment method requires redirect to external service if the user
        get back to site by using the back button, we need to set a flag in localStorage
    */
    if (localStorage && paymentsResponse?.action?.type === 'redirect') {
        localStorage.setItem('isRedirectedToExternalPayment', 'true')
    }
    return {paymentsResponse: paymentsResponse}
}

export const onAdditionalDetails = async (state, component, props) => {
    const adyenPaymentsDetailsService = new AdyenPaymentsDetailsService(props?.token, props?.site)
    const paymentsDetailsResponse = await adyenPaymentsDetailsService.submitPaymentsDetails(
        state.data,
        props?.customerId
    )
    return {paymentsDetailsResponse: paymentsDetailsResponse}
}

export const getAmount = ({basket}) => {
    if (!basket) return null

    // export conversion rate fields from basket
    const conversionsRate = {
        conversionRate: basket?.c_conversionRate,
        lcpMultiplier: basket?.c_lcpMultiplier,
        paymentCurrencyCode: basket?.c_paymentCurrencyCode,
        usePriceConverter: basket?.c_usePriceConverter || false,
        isInternationalOrder: basket?.c_isInternationalOrder || false
    }

    const finalTotal =
        basket?.c_isInternationalOrder && basket?.c_internationalShippingOrderQuote
            ? basket?.c_basketTotalWithGiftCertificatesApplied ||
              basket?.c_internationalShippingOrderQuote
            : getConvertedPrice(
                  basket?.c_basketTotalWithGiftCertificatesApplied,
                  conversionsRate,
                  false
              )

    return {
        value: getCurrencyValueForApi(
            finalTotal,
            conversionsRate?.usePriceConverter ? basket?.c_paymentCurrencyCode : basket.currency
        ),
        currency: getConvertedCurrencyCode(conversionsRate, basket.currency)
    }
}
